import React from 'react';

export interface LogoProps {
  className?: string;
  size?: number;
  iconOnly?: boolean;
  hideDot?: boolean;
  showSubtitle?: boolean;
  variant?: 'caps' | 'mixed';
  asSvg?: boolean;
}

export const Logo = ({
  className = '',
  size = 38,
  iconOnly = false,
  hideDot = false,
  showSubtitle = true,
  variant = 'caps',
  asSvg = false,
}: LogoProps) => {
  // Keyframe styles for pulsing notification beacon
  const pulseStyle = (
    <style>{`
      @keyframes pb-logo-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.35; transform: scale(0.85); }
      }
      .pb-blinking-dot {
        animation: pb-logo-pulse 2s infinite ease-in-out;
        transform-origin: 85px 22px;
      }
    `}</style>
  );

  // SVG Icon component - tightly cropped viewBox (6 15 88 98) for maximum crispness & scale
  const renderIconSvg = (iconDimension: number) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="6 15 88 98"
      width={iconDimension}
      height={iconDimension}
      className="shrink-0 h-auto drop-shadow-[0_2px_8px_rgba(37,99,235,0.22)] select-none"
      aria-label="PostBrix Logo Icon"
      role="img"
    >
      {pulseStyle}

      {/* Subtle Ground Contact Shadow */}
      <ellipse
        cx="50"
        cy="100"
        rx="34"
        ry="9"
        fill="rgba(0,0,0,0.14)"
        className="dark:fill-black/45"
      />

      {/* 3D Isometric Cube Facets */}
      {/* Left shaded face */}
      <polygon points="10,50 50,70 50,98 10,78" fill="#1D4ED8" />
      {/* Right medium face */}
      <polygon points="50,70 90,50 90,78 50,98" fill="#2563EB" />
      {/* Top illuminated face */}
      <polygon points="50,30 90,50 50,70 10,50" fill="#3B82F6" />
      {/* Inner top reflection/facet */}
      <polygon points="50,38 78,50 50,62 22,50" fill="#93C5FD" />

      {/* Pulsing Beacon Dot with contrast ring */}
      {!hideDot && (
        <circle
          className="pb-blinking-dot stroke-white dark:stroke-[#070b14]"
          cx="85"
          cy="22"
          r="5"
          fill="#EF4444"
          strokeWidth="1.5"
        />
      )}
    </svg>
  );

  // Icon only mode
  if (iconOnly) {
    return (
      <div className={`inline-flex items-center justify-center select-none ${className}`}>
        {renderIconSvg(size)}
      </div>
    );
  }

  // Pure SVG fallback mode
  if (asSvg) {
    const calculatedWidth = Math.round(size * (showSubtitle ? 3.8 : 3.6));
    return (
      <div className={`inline-flex items-center select-none ${className}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox={showSubtitle ? "0 0 380 100" : "0 0 360 80"}
          width={calculatedWidth}
          height={size}
          className="h-auto max-w-full"
          aria-label="PostBrix Email Studio"
          role="img"
        >
          {pulseStyle}
          <g transform="translate(0, 0)">
            <ellipse cx="50" cy="85" rx="34" ry="9" fill="rgba(0,0,0,0.14)" className="dark:fill-black/45" />
            <polygon points="10,38 50,56 50,84 10,65" fill="#1D4ED8" />
            <polygon points="50,56 90,38 90,65 50,84" fill="#2563EB" />
            <polygon points="50,20 90,38 50,56 10,38" fill="#3B82F6" />
            <polygon points="50,27 78,38 50,48 22,38" fill="#93C5FD" />
            {!hideDot && (
              <circle
                className="pb-blinking-dot"
                cx="85"
                cy="14"
                r="4.5"
                fill="#EF4444"
                stroke="#FFFFFF"
                strokeWidth="1.5"
              />
            )}
          </g>
          <text
            x="105"
            y={showSubtitle ? "54" : "50"}
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize={showSubtitle ? "46" : "50"}
            fontWeight="900"
            letterSpacing="-0.03em"
          >
            <tspan className="fill-[#0F172A] dark:fill-[#FFFFFF]">
              {variant === 'mixed' ? 'Post' : 'POST'}
            </tspan>
            <tspan className="fill-[#1D4ED8] dark:fill-[#3B82F6]">
              {variant === 'mixed' ? 'Brix' : 'BRIX'}
            </tspan>
          </text>
          {showSubtitle && (
            <text
              x="108"
              y="78"
              fontFamily="system-ui, -apple-system, sans-serif"
              fontSize="18"
              fontWeight="700"
              className="fill-[#64748B] dark:fill-[#94A3B8]"
              letterSpacing="0.18em"
            >
              EMAIL STUDIO
            </text>
          )}
        </svg>
      </div>
    );
  }

  // Modern SaaS HTML + Vector SVG Lockup
  // Dynamic golden typography proportions
  const titleFontSize = showSubtitle
    ? Math.round(size * 0.55) // e.g. 38 -> 21px, 42 -> 23px
    : Math.round(size * 0.62); // e.g. 38 -> 24px, 42 -> 26px
  
  const subtitleFontSize = Math.max(7.5, Math.round(size * 0.23)); // e.g. 38 -> 9px, 42 -> 10px
  const subtitleMargin = Math.max(1, Math.round(size * 0.08)); // e.g. 38 -> 3px

  return (
    <div className={`inline-flex items-center gap-2.5 sm:gap-3 select-none ${className}`}>
      {renderIconSvg(size)}

      <div className="flex flex-col justify-center leading-none">
        <div className="flex items-center">
          <span
            className="font-black tracking-[-0.03em] text-[#0F172A] dark:text-[#FFFFFF] transition-colors leading-none"
            style={{ fontSize: `${titleFontSize}px` }}
          >
            {variant === 'mixed' ? (
              <>
                Post<span className="text-[#1D4ED8] dark:text-[#3B82F6]">Brix</span>
              </>
            ) : (
              <>
                POST<span className="text-[#1D4ED8] dark:text-[#3B82F6]">BRIX</span>
              </>
            )}
          </span>
        </div>

        {showSubtitle && (
          <span
            className="font-bold uppercase tracking-[0.18em] text-[#64748B] dark:text-[#94A3B8] select-none leading-none transition-colors"
            style={{
              fontSize: `${subtitleFontSize}px`,
              marginTop: `${subtitleMargin}px`,
            }}
          >
            EMAIL STUDIO
          </span>
        )}
      </div>
    </div>
  );
};